import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus,
    Search,
    Trash2,
    MessageSquare,
    Copy,
    CheckCircle2,
    User,
    FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ACCESS_LEVELS } from '../../utils/adminUtils';
import { safeParseArray } from '../../utils/storageUtils';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const StaffResponsePatterns = () => {
    const { user } = useAuth();
    const [patterns, setPatterns] = useState([]);
    const [search, setSearch] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        role: '',
        content: ''
    });

    useEffect(() => {
        const fetchPatterns = async () => {
            try {
                const { data, error } = await supabase
                    .from('response_patterns')
                    .select('*')
                    .order('created_at', { ascending: false });

                if (error) {
                    console.error('Error fetching response patterns:', error);
                } else {
                    const mappedData = (data || []).map(item => ({
                        id: item.id,
                        title: item.title,
                        content: item.content,
                        role: item.role_required,
                        createdAt: item.created_at
                    }));
                    setPatterns(mappedData);
                }
            } catch (err) {
                console.error('Unexpected error loading patterns:', err);
            }
        };

        fetchPatterns();

        // Realtime subscription
        const channel = supabase.channel('response-patterns-realtime')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'response_patterns' }, fetchPatterns)
            .subscribe();

        return () => supabase.removeChannel(channel);
    }, []);

    const handleCreate = async () => {
        if (!formData.title || !formData.role || !formData.content) {
            toast.error('Preencha todos os campos obrigatórios.');
            return;
        }

        try {
            const { error } = await supabase
                .from('response_patterns')
                .insert([{
                    title: formData.title,
                    content: formData.content,
                    role_required: formData.role
                }]);

            if (error) throw error;

            setIsCreating(false);
            setFormData({ title: '', role: '', content: '' });
            toast.success('Padrão de resposta criado com sucesso!');
        } catch (error) {
            console.error(error);
            toast.error('Erro ao criar padrão de resposta.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este padrão?')) {
            try {
                const { error } = await supabase
                    .from('response_patterns')
                    .delete()
                    .eq('id', id);

                if (error) throw error;
                toast.success('Padrão removido.');
            } catch (error) {
                console.error(error);
                toast.error('Erro ao excluir padrão.');
            }
        }
    };

    const handleCopy = (content) => {
        navigator.clipboard.writeText(content);
        toast.success('Resposta copiada para a área de transferência!', {
            icon: <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        });
    };

    const filteredPatterns = patterns.filter(p =>
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        p.content.toLowerCase().includes(search.toLowerCase()) ||
        ACCESS_LEVELS[p.role]?.label.toLowerCase().includes(search.toLowerCase())
    );

    // Get list of roles for selection
    const availableRoles = Object.entries(ACCESS_LEVELS).map(([key, value]) => ({
        id: key,
        label: value.label,
        color: value.color
    }));

    return (
        <Card className="bg-black/40 backdrop-blur-xl border-white/10 shadow-2xl overflow-hidden min-h-[600px]">
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] -z-10" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[120px] -z-10" />

            <CardHeader className="border-b border-white/5 pb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <CardTitle className="text-2xl font-bold text-white flex items-center gap-3">
                            <MessageSquare className="h-6 w-6 text-blue-400" />
                            Padrões de Ticket
                        </CardTitle>
                        <CardDescription className="text-gray-400 mt-1">
                            Modelos de resposta padronizados para a equipe
                        </CardDescription>
                    </div>

                    <Dialog open={isCreating} onOpenChange={setIsCreating}>
                        <DialogTrigger asChild>
                            <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25 border-0 rounded-xl px-6 h-11 transition-all hover:scale-105 active:scale-95">
                                <Plus className="h-5 w-5 mr-2" />
                                Criar Padrão
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-zinc-950 border-white/10 text-white sm:max-w-[500px]">
                            <DialogHeader>
                                <DialogTitle className="text-xl flex items-center gap-2">
                                    <FileText className="h-5 w-5 text-blue-400" />
                                    Novo Padrão de Resposta
                                </DialogTitle>
                                <DialogDescription className="text-gray-400">
                                    Crie um modelo de resposta para agilizar o atendimento.
                                </DialogDescription>
                            </DialogHeader>

                            <div className="grid gap-4 py-4">
                                <div className="space-y-2">
                                    <Label className="text-xs font-semibold uppercase text-gray-400">Cargo Responsável</Label>
                                    <Select
                                        value={formData.role}
                                        onValueChange={(value) => setFormData({ ...formData, role: value })}
                                    >
                                        <SelectTrigger className="bg-black/50 border-white/10 text-white">
                                            <SelectValue placeholder="Selecione o cargo" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-white/10 text-white">
                                            {availableRoles.map((role) => (
                                                <SelectItem key={role.id} value={role.id}>
                                                    {role.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs font-semibold uppercase text-gray-400">Título do Padrão</Label>
                                    <Input
                                        placeholder="Ex: Saudação Inicial - Suporte"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        className="bg-black/50 border-white/10 focus:border-blue-500/50"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-xs font-semibold uppercase text-gray-400">Conteúdo da Resposta</Label>
                                    <Textarea
                                        placeholder="Digite a resposta padrão aqui..."
                                        value={formData.content}
                                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                        className="bg-black/50 border-white/10 focus:border-blue-500/50 min-h-[150px]"
                                    />
                                </div>
                            </div>

                            <DialogFooter>
                                <Button variant="ghost" onClick={() => setIsCreating(false)} className="border-white/10 text-gray-400 hover:text-white hover:bg-white/5">Cancelar</Button>
                                <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700 text-white">Criar Padrão</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </CardHeader>

            <CardContent className="p-6 space-y-6">
                {/* Search Bar */}
                <div className="relative group max-w-xl">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-400 transition-colors" />
                        <Input
                            placeholder="Pesquisar por título, conteúdo ou cargo..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-12 bg-black/50 border-white/10 text-white h-12 rounded-xl focus:border-blue-500/50 focus:ring-blue-500/20 transition-all placeholder:text-gray-600"
                        />
                    </div>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <AnimatePresence>
                        {filteredPatterns.map((pattern) => (
                            <motion.div
                                key={pattern.id}
                                initial={{ opacity: 0, scale: 0.98 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="group relative bg-white/5 hover:bg-white/[0.07] border border-white/5 hover:border-blue-500/30 rounded-xl p-5 transition-all duration-300 flex flex-col gap-4"
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400">
                                            <FileText className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <Badge variant="outline" className="bg-blue-500/10 text-blue-300 border-blue-500/20 text-[10px] uppercase">
                                                    {ACCESS_LEVELS[pattern.role]?.label || pattern.role}
                                                </Badge>
                                            </div>
                                            <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors">
                                                {pattern.title}
                                            </h3>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => handleCopy(pattern.content)}
                                            className="h-8 w-8 text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg"
                                            title="Copiar Resposta"
                                        >
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => handleDelete(pattern.id)}
                                            className="h-8 w-8 text-gray-500 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="bg-black/20 rounded-lg p-3 border border-white/5 relative group/content">
                                    <p className="text-gray-300 text-sm leading-relaxed line-clamp-4 group-hover/content:line-clamp-none transition-all">
                                        {pattern.content}
                                    </p>
                                </div>

                                <Button
                                    className="w-full bg-white/5 hover:bg-blue-500/20 hover:text-blue-300 border border-white/5 hover:border-blue-500/30 transition-all"
                                    onClick={() => handleCopy(pattern.content)}
                                >
                                    <Copy className="h-4 w-4 mr-2" />
                                    Copiar e Colar
                                </Button>
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {filteredPatterns.length === 0 && (
                        <div className="col-span-full py-12 text-center flex flex-col items-center justify-center opacity-50">
                            <MessageSquare className="h-12 w-12 text-gray-600 mb-4" />
                            <p className="text-gray-400 text-lg">Nenhum padrão de resposta encontrado.</p>
                            {search && <p className="text-gray-600 text-sm mt-1">Tente buscar por outros termos.</p>}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default StaffResponsePatterns;
